import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { Header } from "@/components/Header";
import { PropertyCard } from "@/components/PropertyCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SocialLinks, useSettings } from "@/components/SocialLinks";
import { Logo } from "@/components/Logo";
import { InstallButton } from "@/components/Pwa";
import { GoogleReviews } from "@/components/GoogleReviews";
import { Search, MapPin, Users, Leaf, Phone, Mail } from "lucide-react";

export default function Explore() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [location, setLocation] = useState("");
  const [guests, setGuests] = useState("");
  const [sort, setSort] = useState("default");
  const [searched, setSearched] = useState(false);
  const settings = useSettings();
  const home = settings?.home || {};

  const load = async (params = {}, s = sort) => {
    setLoading(true);
    try {
      if (s && s !== "default") params.sort = s;
      const { data } = await api.get("/listings", { params });
      setListings(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const currentParams = () => {
    const params = {};
    if (location) params.location = location;
    if (guests) params.guests = Number(guests);
    return params;
  };

  const onSearch = (e) => {
    e.preventDefault();
    setSearched(true);
    load(currentParams());
    setTimeout(() => document.getElementById("logements")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const resetSearch = () => { setLocation(""); setGuests(""); setSearched(false); load({}); };

  const onSort = (v) => { setSort(v); load(currentParams(), v); };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <section className="relative">
        <div className="relative h-[560px] md:h-[620px] overflow-hidden">
          {home.hero_image && <img src={home.hero_image} alt="" className="w-full h-full object-cover" data-testid="hero-image" />}
          <div className="absolute inset-0 bg-gradient-to-t from-olive-dark/85 via-olive-dark/30 to-transparent" />
          <div className="absolute inset-0 flex flex-col justify-center max-w-7xl mx-auto px-5 md:px-8">
            <div className="max-w-2xl fade-up">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/80 mb-4" data-testid="hero-tagline">
                <Leaf className="w-4 h-4" /> {home.tagline || "Séjours au cœur de la nature"}
              </span>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-none tracking-tight" data-testid="hero-headline">
                {home.headline || "L’Orne Park"}
              </h1>
              <p className="text-white/85 mt-5 text-base md:text-lg max-w-xl" data-testid="hero-intro">
                {home.intro}
              </p>
              {home.location && <p className="text-white/70 mt-4 text-sm inline-flex items-center gap-1.5" data-testid="hero-location"><MapPin className="w-4 h-4" /> {home.location}</p>}
              <div className="mt-6"><InstallButton variant="secondary" className="bg-white/90 hover:bg-white border-0" /></div>
            </div>
          </div>
        </div>

        <form
          onSubmit={onSearch}
          className="relative -mt-12 md:-mt-10 z-10 max-w-4xl mx-auto px-5"
          data-testid="search-bar"
        >
          <div className="bg-white rounded-2xl md:rounded-full shadow-xl border border-border p-3 flex flex-col md:flex-row items-stretch md:items-center gap-3">
            <div className="flex items-center gap-2 flex-1 px-3">
              <MapPin className="w-5 h-5 text-rose-brand" />
              <Select value={location || "all"} onValueChange={(v) => setLocation(v === "all" ? "" : v)}>
                <SelectTrigger className="border-0 shadow-none focus:ring-0 px-0 h-10" data-testid="search-destination-select"><SelectValue placeholder="Destination" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">Toutes les destinations</SelectItem>
                  {(home.destinations || ["Rosselange", "Rombas"]).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="hidden md:block w-px h-8 bg-border" />
            <div className="flex items-center gap-2 md:w-44 px-3">
              <Users className="w-5 h-5 text-rose-brand" />
              <Input
                type="number" min="1"
                value={guests}
                onChange={(e) => setGuests(e.target.value)}
                placeholder="Voyageurs"
                className="border-0 shadow-none focus-visible:ring-0 px-0"
                data-testid="search-guests-input"
              />
            </div>
            <Button type="submit" className="bg-rose-brand hover:bg-rosebrand-soft text-white rounded-full h-12 px-8" data-testid="search-submit">
              <Search className="w-4 h-4 mr-2" /> Rechercher
            </Button>
          </div>
        </form>
      </section>

      {home.about && (
        <section className="max-w-4xl mx-auto px-5 md:px-8 pt-16" data-testid="about-section">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-rose-brand mb-3">Bienvenue</span>
          <div className="font-serif text-lg md:text-xl text-olive leading-relaxed whitespace-pre-line">{home.about}</div>
        </section>
      )}

      {home.highlights?.some((h) => h.title) && (
        <section className="max-w-7xl mx-auto px-5 md:px-8 pt-16" data-testid="highlights-section">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {home.highlights.filter((h) => h.title).map((h, i) => (
              <div key={i} className="rounded-2xl bg-white border border-border p-6 hover-lift" style={{ animationDelay: `${i * 80}ms` }}>
                <span className="w-9 h-9 rounded-full bg-rosebrand-pale text-rose-brand flex items-center justify-center font-serif text-lg">{i + 1}</span>
                <h3 className="font-serif text-xl text-olive mt-3">{h.title}</h3>
                <p className="text-sm text-muted-foreground mt-1">{h.text}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section id="logements" className="max-w-7xl mx-auto px-5 md:px-8 py-16 scroll-mt-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-serif text-3xl md:text-4xl text-olive">Nos logements</h2>
            <p className="text-muted-foreground mt-1">Sélection de séjours disponibles</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-muted-foreground">{listings.length} logement(s)</span>
            <Select value={sort} onValueChange={onSort}>
              <SelectTrigger className="w-48 bg-white rounded-full" data-testid="sort-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="default">Tri par défaut</SelectItem>
                <SelectItem value="rating">Meilleures notes</SelectItem>
                <SelectItem value="price_asc">Prix croissant</SelectItem>
                <SelectItem value="price_desc">Prix décroissant</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {searched && (
          <div className="mb-4 flex items-center gap-3 text-sm" data-testid="search-summary">
            <span className="px-3 py-1 rounded-full bg-rosebrand-pale text-rose-brand font-medium">
              {listings.length} résultat(s){location ? ` · ${location}` : ""}{guests ? ` · ${guests} voyageur(s)` : ""}
            </span>
            <button onClick={resetSearch} className="text-muted-foreground underline hover:text-olive" data-testid="search-reset">Tout afficher</button>
          </div>
        )}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="aspect-[4/3] rounded-2xl bg-secondary animate-pulse" />
            ))}
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-20" data-testid="no-results">
            <p className="text-muted-foreground">Aucun logement ne correspond à votre recherche.</p>
            <button onClick={resetSearch} className="mt-3 text-rose-brand font-semibold underline">Voir tous les logements</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="listings-grid">
            {listings.map((l, i) => (
              <PropertyCard key={l.listing_id} listing={l} index={i} />
            ))}
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-16"><GoogleReviews compact /></section>

      <footer className="bg-olive-dark text-white/80 py-12 mt-8">
        <div className="max-w-7xl mx-auto px-5 md:px-8 flex flex-col md:flex-row justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Logo size={36} />
              <span className="font-serif text-xl text-white">L’Orne Park</span>
            </div>
            <p className="text-sm max-w-sm">{home.footer_text}</p>
            <div className="flex flex-wrap gap-4 mt-3 text-sm" data-testid="footer-contact">
              {home.location && <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {home.location}</span>}
              {home.phone && <a href={`tel:${home.phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-1.5 hover:text-white"><Phone className="w-4 h-4" /> {home.phone}</a>}
              {home.contact_email && <a href={`mailto:${home.contact_email}`} className="inline-flex items-center gap-1.5 hover:text-white"><Mail className="w-4 h-4" /> {home.contact_email}</a>}
            </div>
          </div>
          <div className="flex flex-col items-start md:items-end gap-2">
            <SocialLinks light />
            <p className="text-sm">© 2026 L’Orne Park</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
